// @flow
import React from 'react';
import identityKey from 'identity-keys';
import { eachBlock } from 'message-parsing';
import MessageBlock from './MessageBlock';
import type { ServiceBubblePropType } from './ServiceBubblePropType';

type MessagePayloadProps = {
  /**
   * A message body payload, in plain text.
   */
  payload: string,

  /**
   * Should we process our special URL format? (see `message-parsing/README`
   * for details)
   *
   * Currently, we only expect Bookkeepers to be able to use this URL format,
   * so this should probably be set to `false` for regular users.
   *
   * If `true`, any URLs found in `payload` will be handled as follows:
   *
   * - The rendered link's `href` will be stripped of any parameters
   * starting with the `bl.` prefix.
   * - If the `bl.title` param is found, it will be used for the link
   * text shown to the user (instead of the raw URL).
   * - If the `bl.serviceBubble` param is found, *and* matches one of the keys
   * provided by the `serviceBubble` prop (see below), render the associated
   * component instead of the link.
   */
  handleLinkFormat: boolean,

  /**
   * Pass in an object mapping `serviceBubbleType` strings to a React component
   * (class or function) to render service bubbles inside this message.
   *
   * These components will be given `ServiceBubbleProps` - see
   * `ServiceBubblePropType` for more details.
   *
   * This prop is only used if `handleLinkFormat` is set.
   *
   */
  serviceBubbles?: ServiceBubblePropType,
};
/**
 * Render message content, optionally handling our special link format,
 * and replacing any `serviceBubble` links with the given components.
 *
 * @param {MessagePayloadProps} props
 */
export default function MessagePayload({
  payload,
  handleLinkFormat,
  serviceBubbles,
}: MessagePayloadProps) {
  // eslint-disable-line object-curly-newline
  return Array.from(eachBlock(payload)).map(block => (
    <MessageBlock
      key={identityKey(block)}
      block={block}
      serviceBubbles={serviceBubbles}
      handleLinkFormat={handleLinkFormat}
    />
  ));
}
MessagePayload.defaultProps = {
  serviceBubbles: {},
};
